import React, { useState, Component } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Moon, Sun, Monitor, Bot, Shield, Wifi, Save, RefreshCw, Palette, Tag, Building2, Sparkles, RotateCcw, Check, Copy, Eye, EyeOff, Lock, Key, Link, Mail, AlertTriangle, ChevronDown, ChevronRight, TestTube, CheckCircle2, Rocket, X } from 'lucide-react';
import { useBrandConfig } from '../../hooks/useBrandConfig';
import { config } from '../../services/config';
import { UserProfile, userProfiles } from '../../services/userProfiles';
import { secureStorage, KEYS } from '../../services/secureStorage';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { getSlackToken, setSlackToken } from '../../services/slack';
import { NotificationPreferences } from './NotificationPreferences';
// ─── Secret Field ────────────────────────────────────────────────────────────
function SecretField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  locked = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  locked?: boolean;
}) {
  const [show, setShow] = useState(false);
  return <div data-id="element-6102">
      <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6103">
        {label}
      </label>
      <div className="relative" data-id="element-6104">
        <input type={show && !locked ? 'text' : 'password'} className={`w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-opacity ${locked ? 'opacity-40 cursor-not-allowed select-none' : ''}`} value={locked ? '••••••••••••••••••••' : value} onChange={e => !locked && onChange(e.target.value)} placeholder={placeholder} disabled={locked} readOnly={locked} data-id="element-6105" />

        <button type="button" onClick={() => !locked && setShow(v => !v)} className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${locked ? 'text-slate-700 cursor-not-allowed' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-6106">
          {locked ? <Lock size={14} data-id="element-6107" /> : show ? <EyeOff size={14} data-id="element-6108" /> : <Eye size={14} data-id="element-6109" />}
        </button>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1" data-id="element-6110">{hint}</p>}
    </div>;
}
// ─── Plain Field ─────────────────────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
  icon: Icon
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  icon?: React.ElementType;
}) {
  return <div data-id="element-6111">
      <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6112">
        {label}
      </label>
      <div className="relative" data-id="element-6113">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-6114" />}
        <input className={`w-full rounded-lg border border-slate-700 bg-slate-800 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${Icon ? 'pl-9 pr-3' : 'px-3'}`} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-id="element-6115" />
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1" data-id="element-6116">{hint}</p>}
    </div>;
}
// ─── Admin Unlock Modal ───────────────────────────────────────────────────────
function AdminUnlockModal({
  onSuccess,
  onCancel
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const handleSubmit = () => {
    if (password === 'admin123' || password === 'Admin1!') {
      onSuccess();
    } else {
      setError('Contraseña incorrecta.');
      setPassword('');
    }
  };
  return <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-6117">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-id="element-6118">
        <div className="flex items-center gap-3 mb-5" data-id="element-6119">
          <div className="p-2.5 bg-amber-950/50 rounded-xl border border-amber-900/50" data-id="element-6120">
            <Lock size={20} className="text-amber-400" data-id="element-6121" />
          </div>
          <div data-id="element-6122">
            <h3 className="text-sm font-bold text-slate-100" data-id="element-6123">
              Acceso Admin Requerido
            </h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-6124">
              Ingresá tu contraseña para ver las credenciales
            </p>
          </div>
        </div>

        <div className="relative mb-3" data-id="element-6125">
          <input type={show ? 'text' : 'password'} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Contraseña de administrador" value={password} onChange={e => {
          setPassword(e.target.value);
          setError('');
        }} onKeyDown={e => e.key === 'Enter' && handleSubmit()} autoFocus data-id="element-6126" />

          <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" data-id="element-6127">
            {show ? <EyeOff size={14} data-id="element-6128" /> : <Eye size={14} data-id="element-6129" />}
          </button>
        </div>

        {error && <div className="flex items-center gap-2 text-xs text-red-400 mb-3 p-2.5 bg-red-950/30 rounded-lg border border-red-900/40" data-id="element-6130">
            <AlertTriangle size={12} data-id="element-6131" /> {error}
          </div>}

        <p className="text-[10px] text-slate-600 mb-4" data-id="element-6132">
          Demo: <span className="font-mono" data-id="element-6133">admin123</span>
        </p>

        <div className="flex gap-2" data-id="element-6134">
          <button onClick={onCancel} className="flex-1 py-2.5 text-sm text-slate-400 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors border border-slate-700" data-id="element-6135">
            Cancelar
          </button>
          <button onClick={handleSubmit} className="flex-1 py-2.5 text-sm text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors font-semibold" data-id="element-6136">
            Verificar
          </button>
        </div>
      </div>
    </div>;
}
// ─── Coming Soon Modal ────────────────────────────────────────────────────────
function ComingSoonModal({
  tool,
  onClose
}: {
  tool: {
    name: string;
    color: string;
    bg: string;
    description: string;
    eta: string;
  };
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-id="element-6137">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-center" onClick={e => e.stopPropagation()} data-id="element-6138">
        {/* Animated icon */}
        <div className={`w-16 h-16 rounded-2xl ${tool.bg} border mx-auto mb-5 flex items-center justify-center`} data-id="element-6139">
          <Rocket size={28} className={tool.color} data-id="element-6140" />
        </div>

        {/* Badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-950/60 border border-indigo-800/60 text-indigo-300 text-xs font-semibold rounded-full mb-4" data-id="element-6141">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" data-id="element-6142"></span>
          En desarrollo
        </span>

        <h3 className="text-xl font-bold text-slate-100 mb-2" data-id="element-6143">{tool.name}</h3>
        <p className="text-sm text-slate-400 leading-relaxed mb-2" data-id="element-6144">
          {tool.description}
        </p>
        <p className="text-xs text-slate-500 mb-6" data-id="element-6145">
          Estimado:{' '}
          <span className="text-indigo-400 font-medium" data-id="element-6146">{tool.eta}</span>
        </p>

        {/* Notify me */}
        <div className="flex gap-2 mb-4" data-id="element-6147">
          <input className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="tu@empresa.com" data-id="element-6148" />

          <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap" data-id="element-6149">
            Notificarme
          </button>
        </div>

        <button onClick={onClose} className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-300 transition-colors flex items-center justify-center gap-1.5" data-id="element-6150">
          <X size={14} data-id="element-6151" /> Cerrar
        </button>
      </div>
    </div>;
}
// ─── Platform Selector ────────────────────────────────────────────────────────
const PM_TOOLS = [{
  id: 'jira',
  name: 'Jira',
  subtitle: 'Atlassian',
  description: 'Issues, sprints y roadmaps',
  color: 'text-blue-400',
  bg: 'bg-blue-950/40 border-blue-900/40',
  activeBg: 'bg-blue-950/60 border-blue-500/60',
  dot: 'bg-blue-400',
  available: true,
  eta: '',
  longDescription: ''
}, {
  id: 'trello',
  name: 'Trello',
  subtitle: 'Atlassian',
  description: 'Tableros visuales y tarjetas',
  color: 'text-sky-400',
  bg: 'bg-sky-950/30 border-sky-900/30',
  activeBg: 'bg-sky-950/60 border-sky-500/60',
  dot: 'bg-sky-400',
  available: false,
  eta: 'Q3 2025',
  longDescription: 'Conectá tus tableros de Trello para gestionar tareas con el agente de forma visual e intuitiva.'
}, {
  id: 'clickup',
  name: 'ClickUp',
  subtitle: 'ClickUp',
  description: 'Todo-en-uno: tasks, docs, goals',
  color: 'text-violet-400',
  bg: 'bg-violet-950/30 border-violet-900/30',
  activeBg: 'bg-violet-950/60 border-violet-500/60',
  dot: 'bg-violet-400',
  available: false,
  eta: 'Q3 2025',
  longDescription: 'Integrá ClickUp para que el agente gestione tareas, objetivos y documentos desde un solo lugar.'
}, {
  id: 'notion',
  name: 'Notion',
  subtitle: 'Notion Labs',
  description: 'Docs, wikis y bases de datos',
  color: 'text-slate-300',
  bg: 'bg-slate-800/50 border-slate-700/50',
  activeBg: 'bg-slate-700/60 border-slate-500/60',
  dot: 'bg-slate-300',
  available: false,
  eta: 'Q4 2025',
  longDescription: 'Conectá Notion para que el agente acceda a tus wikis, bases de datos y documentos del equipo.'
}];
function PlatformSelector({
  selected,
  onSelect
}: {
  selected: string;
  onSelect: (id: string) => void;
}) {
  const [comingSoonTool, setComingSoonTool] = useState<(typeof PM_TOOLS)[0] | null>(null);
  return <>
      {comingSoonTool && <ComingSoonModal tool={comingSoonTool} onClose={() => setComingSoonTool(null)} data-id="element-6152" />}
      <div className="grid grid-cols-2 gap-3" data-id="element-6153">
        {PM_TOOLS.map(tool => <button key={tool.id} onClick={() => tool.available ? onSelect(tool.id) : setComingSoonTool(tool)} className={`relative p-4 rounded-xl border text-left transition-all group ${tool.available && selected === tool.id ? tool.activeBg + ' ring-1 ring-inset ring-blue-500/30' : tool.available ? 'bg-slate-950 border-slate-800 hover:border-slate-600' : 'bg-slate-950/50 border-slate-800/50 cursor-pointer hover:border-slate-700'}`} data-id="element-6154">
            {/* Active indicator */}
            {tool.available && selected === tool.id && <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold text-blue-400" data-id="element-6155">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" data-id="element-6156"></span>
                Activo
              </span>}

            {/* Coming soon badge */}
            {!tool.available && <span className="absolute top-3 right-3 px-1.5 py-0.5 bg-slate-800 text-slate-500 text-[9px] font-semibold rounded border border-slate-700 uppercase tracking-wide" data-id="element-6157">
                Soon
              </span>}

            <div className={`w-8 h-8 rounded-lg ${tool.bg} border flex items-center justify-center mb-3`} data-id="element-6158">
              <span className={`w-3 h-3 rounded-full ${tool.dot} ${!tool.available ? 'opacity-40' : ''}`} data-id="element-6159"></span>
            </div>

            <p className={`text-sm font-bold mb-0.5 ${tool.available ? 'text-slate-100' : 'text-slate-500'}`} data-id="element-6160">
              {tool.name}
            </p>
            <p className={`text-[10px] ${tool.available ? 'text-slate-500' : 'text-slate-600'}`} data-id="element-6161">
              {tool.description}
            </p>
          </button>)}
      </div>
    </>;
}
// ─── Integration Section ──────────────────────────────────────────────────────
function IntegrationSection({
  title,
  icon: Icon,
  iconColor,
  connected,
  onToggle,
  locked,
  children
}: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  connected: boolean;
  onToggle: () => void;
  locked: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(connected);
  return <div className={`rounded-xl border transition-all ${connected ? 'border-slate-700 bg-slate-950' : 'border-slate-800 bg-slate-950/50'}`} data-id="element-6162">
      <div className="flex items-center justify-between p-4" data-id="element-6163">
        <div className="flex items-center gap-3" data-id="element-6164">
          <Icon size={18} className={iconColor} data-id="element-6165" />
          <div data-id="element-6166">
            <p className="text-sm font-semibold text-slate-200" data-id="element-6167">{title}</p>
            <p className="text-xs text-slate-500" data-id="element-6168">
              {connected ? 'Conectado' : 'Desconectado'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-6169">
          {connected && <button onClick={() => setExpanded(v => !v)} className="text-slate-500 hover:text-slate-300 transition-colors p-1" data-id="element-6170">
              {expanded ? <ChevronDown size={16} data-id="element-6171" /> : <ChevronRight size={16} data-id="element-6172" />}
            </button>}
          <button onClick={onToggle} className={`relative w-11 h-6 rounded-full transition-colors ${connected ? 'bg-emerald-600' : 'bg-slate-700'}`} data-id="element-6173">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${connected ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-6174" />
          </button>
        </div>
      </div>
      {connected && expanded && <div className="px-4 pb-4 pt-0 border-t border-slate-800 mt-0 space-y-4" data-id="element-6175">
          <div className="pt-4" data-id="element-6176">
            {locked && <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-950/30 border border-amber-900/40 rounded-lg p-2.5 mb-4" data-id="element-6177">
                <Lock size={12} data-id="element-6178" /> Las credenciales están ocultas. Solo admins
                pueden verlas.
              </div>}
            {children}
          </div>
        </div>}
    </div>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function SettingsPanel({
  activeUser,
  onLogout
}: {
  activeUser?: UserProfile;
  onLogout?: () => void;
}) {
  const {
    brand,
    updateBrand,
    resetBrand
  } = useBrandConfig();
  const [theme, setTheme] = useState<'dark' | 'light' | 'system'>('dark');
  const [tone, setTone] = useState('Professional');
  const [language, setLanguage] = useState('Spanish');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedAccent, setSelectedAccent] = useState(0);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('jira');
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [connectionResults, setConnectionResults] = useState<Record<string, boolean>>({});
  // Integration states - initialize from activeUser if available
  const [jiraConnected, setJiraConnected] = useState(true);
  const [confluenceConnected, setConfluenceConnected] = useState(true);
  const [slackConnected, setSlackConnected] = useState(!!getSlackToken());
  const [githubConnected, setGithubConnected] = useState(true);
  const [jiraConfig, setJiraConfig] = useState({
    baseUrl: activeUser?.jira?.baseUrl || 'https://acme-corp.atlassian.net',
    email: activeUser?.jira?.email || 'develop@agent.ai',
    apiKey: activeUser?.jira?.apiKey || 'ATATT3xFfGF0_mock_api_key_here',
    projectKey: activeUser?.jira?.projectKey || 'VSM'
  });
  const [confluenceConfig, setConfluenceConfig] = useState({
    baseUrl: activeUser?.confluence?.baseUrl || 'https://acme-corp.atlassian.net/wiki',
    email: activeUser?.confluence?.email || 'develop@agent.ai',
    apiKey: activeUser?.confluence?.apiKey || 'ATATT3xFfGF0_mock_api_key_here',
    spaceKey: activeUser?.confluence?.spaceKey || 'VSM'
  });
  const [slackConfig, setSlackConfig] = useState({
    webhookUrl: '',
    botToken: getSlackToken(),
    channel: '#dev-team'
  });
  const [githubConfig, setGithubConfig] = useState({
    token: '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',
    org: activeUser?.github?.org || '',
    repo: ''
  });
  const {
    isConnected: isGoogleConnected,
    user: googleUser,
    signIn: googleSignIn,
    signOut: googleSignOut
  } = useGoogleAuth();
  const handleSave = () => {
    setIsSaving(true);
    // Save Slack token
    if (slackConfig.botToken) {
      setSlackToken(slackConfig.botToken);
      setSlackConnected(true);
    }
    // Save to user profile if active user exists
    if (activeUser) {
      userProfiles.updateJiraConfig(activeUser.email, jiraConfig);
      userProfiles.updateConfluenceConfig(activeUser.email, confluenceConfig);
      // Update secure storage with new credentials
      if (jiraConfig.email && jiraConfig.apiKey) {
        secureStorage.set(KEYS.JIRA_USER, jiraConfig.email);
        secureStorage.set(KEYS.JIRA_PASS, jiraConfig.apiKey);
      }
      if (confluenceConfig.email && confluenceConfig.apiKey) {
        secureStorage.set(KEYS.CONFLUENCE_USER, confluenceConfig.email);
        secureStorage.set(KEYS.CONFLUENCE_PASS, confluenceConfig.apiKey);
      }
      // Save GitHub config
      userProfiles.updateProfile(activeUser.email, {
        github: {
          org: githubConfig.org,
          token: githubConfig.token
        }
      });
      // Apply GitHub config immediately
      try {
        const {
          applyUserGitHubConfig
        } = require('../../services/githubData');
        applyUserGitHubConfig({
          org: githubConfig.org,
          token: githubConfig.token
        });
      } catch {}
      // Save brand overrides
      userProfiles.updateProfile(activeUser.email, {
        brand: {
          name: brand.name,
          company: brand.company,
          logoInitial: brand.logoInitial,
          jiraBaseUrl: brand.jiraBaseUrl
        }
      });
    }
    setTimeout(() => {
      setIsSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    }, 800);
  };
  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };
  const testConnection = (service: string) => {
    setTestingConnection(service);
    setTimeout(() => {
      setTestingConnection(null);
      setConnectionResults(prev => ({
        ...prev,
        [service]: true
      }));
      setTimeout(() => setConnectionResults(prev => {
        const n = {
          ...prev
        };
        delete n[service];
        return n;
      }), 3000);
    }, 1500);
  };
  const accentColors = [{
    name: 'Indigo',
    class: 'bg-indigo-500',
    value: 'indigo'
  }, {
    name: 'Emerald',
    class: 'bg-emerald-500',
    value: 'emerald'
  }, {
    name: 'Blue',
    class: 'bg-blue-500',
    value: 'blue'
  }, {
    name: 'Violet',
    class: 'bg-violet-500',
    value: 'violet'
  }, {
    name: 'Rose',
    class: 'bg-rose-500',
    value: 'rose'
  }];
  const apiEndpoints = [{
    label: 'API Gateway',
    value: config.api.baseUrl,
    key: 'api'
  }, {
    label: 'Lambda URL',
    value: config.api.lambdaUrl,
    key: 'lambda'
  }, {
    label: 'Tenant ID',
    value: config.api.tenantId,
    key: 'tenant'
  }, {
    label: 'Project Key',
    value: config.api.projectKey,
    key: 'project'
  }];
  return <div className="h-full overflow-y-auto p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-6179">
      {showAdminModal && <AdminUnlockModal onSuccess={() => {
      setAdminUnlocked(true);
      setShowAdminModal(false);
    }} onCancel={() => setShowAdminModal(false)} data-id="element-6180" />}

      <div className="max-w-5xl mx-auto space-y-8" data-id="element-6181">
        {/* Header */}
        <div className="flex justify-between items-start" data-id="element-6182">
          <div data-id="element-6183">
            <h1 className="text-3xl font-bold text-slate-100 mb-2" data-id="element-6184">
              Configuración
            </h1>
            <p className="text-slate-400" data-id="element-6185">
              Personaliza tu experiencia, marca y comportamiento del agente.
            </p>
          </div>
          <div className="flex gap-2 items-center" data-id="element-6186">
            {/* Admin lock/unlock */}
            <button onClick={() => adminUnlocked ? setAdminUnlocked(false) : setShowAdminModal(true)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${adminUnlocked ? 'bg-amber-950/30 border-amber-900/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-slate-500 hover:border-slate-600'}`} data-id="element-6187">
              {adminUnlocked ? <>
                  <Eye size={13} data-id="element-6188" /> Admin activo
                </> : <>
                  <Lock size={13} data-id="element-6189" /> Ver credenciales
                </>}
            </button>
            <Button variant="outline" size="sm" onClick={resetBrand} className="gap-2 text-slate-400" data-id="element-6190">
              <RotateCcw size={14} data-id="element-6191" /> Reset
            </Button>
            <Button size="sm" onClick={handleSave} isLoading={isSaving} className="gap-2" data-id="element-6192">
              {saveSuccess ? <>
                  <Check size={14} data-id="element-6193" /> Guardado!
                </> : <>
                  <Save size={14} data-id="element-6194" /> Guardar Todo
                </>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-6195">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6" data-id="element-6196">
            {/* WHITE LABEL */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6197">
              <CardHeader data-id="element-6198">
                <CardTitle className="flex items-center gap-2" data-id="element-6199">
                  <Tag size={20} className="text-indigo-400" data-id="element-6200" /> White Label /
                  Marca
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-6201">
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-6202">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6203">
                    Vista Previa
                  </p>
                  <div className="flex items-center gap-4" data-id="element-6204">
                    <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-900/30" data-id="element-6205">
                      {brand.logoInitial}
                    </div>
                    <div data-id="element-6206">
                      <h3 className="text-lg font-bold text-slate-100" data-id="element-6207">
                        {brand.name}
                      </h3>
                      <p className="text-sm text-slate-400" data-id="element-6208">{brand.tagline}</p>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-6209">
                        {brand.company} · {brand.footerText}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-6210">
                  {[{
                  label: 'Nombre del Producto',
                  key: 'name' as const,
                  placeholder: 'Agent Pro'
                }, {
                  label: 'Inicial del Logo',
                  key: 'logoInitial' as const,
                  placeholder: 'A'
                }, {
                  label: 'Nombre de Empresa',
                  key: 'company' as const,
                  placeholder: 'Mi Empresa'
                }, {
                  label: 'Project URL',
                  key: 'jiraBaseUrl' as const,
                  placeholder: 'https://tu-herramienta.com/...'
                }].map(({
                  label,
                  key,
                  placeholder
                }) => <div key={key} data-id="element-6211">
                      <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6212">
                        {label}
                      </label>
                      <input className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={brand[key]} onChange={e => updateBrand({
                    [key]: e.target.value
                  })} placeholder={placeholder} data-id="element-6213" />
                    </div>)}
                </div>
                <div data-id="element-6214">
                  <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6215">
                    Tagline
                  </label>
                  <input className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={brand.tagline} onChange={e => updateBrand({
                  tagline: e.target.value
                })} placeholder="Tu Scrum Master Virtual Inteligente" data-id="element-6216" />
                </div>
              </CardContent>
            </Card>

            {/* PLATFORM SELECTOR */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6217">
              <CardHeader data-id="element-6218">
                <CardTitle className="flex items-center gap-2" data-id="element-6219">
                  <Rocket size={20} className="text-indigo-400" data-id="element-6220" /> Plataforma de
                  Gestión
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-6221">
                <p className="text-xs text-slate-500" data-id="element-6222">
                  Elegí la herramienta de gestión de proyectos que usa tu
                  equipo. Más integraciones próximamente.
                </p>
                <PlatformSelector selected={selectedPlatform} onSelect={setSelectedPlatform} data-id="element-6223" />
              </CardContent>
            </Card>

            {/* INTEGRATIONS CONFIG */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6224">
              <CardHeader data-id="element-6225">
                <CardTitle className="flex items-center gap-2" data-id="element-6226">
                  <Key size={20} className="text-indigo-400" data-id="element-6227" /> Configuración de
                  Integraciones
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-6228">
                <p className="text-xs text-slate-500" data-id="element-6229">
                  Configurá las credenciales de cada herramienta. Las API keys
                  están enmascaradas por defecto.
                  {!adminUnlocked && <button onClick={() => setShowAdminModal(true)} className="text-indigo-400 hover:underline ml-1" data-id="element-6230">
                      Desbloqueá como admin para verlas.
                    </button>}
                </p>

                {/* JIRA */}
                <IntegrationSection title="Jira Software" icon={Sparkles} iconColor="text-blue-400" connected={jiraConnected} onToggle={() => setJiraConnected(v => !v)} locked={!adminUnlocked} data-id="element-6231">
                  <div className="space-y-3" data-id="element-6232">
                    <Field label="Base URL" value={jiraConfig.baseUrl} onChange={v => setJiraConfig(p => ({
                    ...p,
                    baseUrl: v
                  }))} placeholder="https://tu-empresa.atlassian.net" icon={Link} data-id="element-6233" />

                    <Field label="Email" value={jiraConfig.email} onChange={v => setJiraConfig(p => ({
                    ...p,
                    email: v
                  }))} placeholder="tu@empresa.com" icon={Mail} data-id="element-6234" />

                    <SecretField label="API Key" value={jiraConfig.apiKey} onChange={v => setJiraConfig(p => ({
                    ...p,
                    apiKey: v
                  }))} placeholder="ATATT3xFfGF0..." hint="Generá tu API key en id.atlassian.com/manage-profile/security/api-tokens" locked={!adminUnlocked} data-id="element-6235" />

                    <Field label="Project Key" value={jiraConfig.projectKey} onChange={v => setJiraConfig(p => ({
                    ...p,
                    projectKey: v
                  }))} placeholder="VSM" icon={Key} data-id="element-6236" />

                    <button onClick={() => testConnection('jira')} disabled={testingConnection === 'jira'} className="flex items-center gap-2 text-xs text-slate-400 hover:text-indigo-400 transition-colors" data-id="element-6237">
                      {testingConnection === 'jira' ? <RefreshCw size={12} className="animate-spin" data-id="element-6238" /> : connectionResults['jira'] ? <CheckCircle2 size={12} className="text-emerald-400" data-id="element-6239" /> : <TestTube size={12} data-id="element-6240" />}
                      {testingConnection === 'jira' ? 'Probando...' : connectionResults['jira'] ? 'Conexión exitosa' : 'Probar conexión'}
                    </button>
                  </div>
                </IntegrationSection>

                {/* CONFLUENCE */}
                <IntegrationSection title="Confluence" icon={Sparkles} iconColor="text-blue-400" connected={confluenceConnected} onToggle={() => setConfluenceConnected(v => !v)} locked={!adminUnlocked} data-id="element-6241">
                  <div className="space-y-3" data-id="element-6242">
                    <Field label="Base URL" value={confluenceConfig.baseUrl} onChange={v => setConfluenceConfig(p => ({
                    ...p,
                    baseUrl: v
                  }))} placeholder="https://tu-empresa.atlassian.net/wiki" icon={Link} data-id="element-6243" />

                    <Field label="Email" value={confluenceConfig.email} onChange={v => setConfluenceConfig(p => ({
                    ...p,
                    email: v
                  }))} placeholder="tu@empresa.com" icon={Mail} data-id="element-6244" />

                    <SecretField label="API Key" value={confluenceConfig.apiKey} onChange={v => setConfluenceConfig(p => ({
                    ...p,
                    apiKey: v
                  }))} placeholder="ATATT3xFfGF0..." hint="Podés usar la misma API key de Jira (mismo tenant Atlassian)" locked={!adminUnlocked} data-id="element-6245" />

                    <Field label="Space Key" value={confluenceConfig.spaceKey} onChange={v => setConfluenceConfig(p => ({
                    ...p,
                    spaceKey: v
                  }))} placeholder="VSM" icon={Key} data-id="element-6246" />

                    <button onClick={() => testConnection('confluence')} disabled={testingConnection === 'confluence'} className="flex items-center gap-2 text-xs text-slate-400 hover:text-indigo-400 transition-colors" data-id="element-6247">
                      {testingConnection === 'confluence' ? <RefreshCw size={12} className="animate-spin" data-id="element-6248" /> : connectionResults['confluence'] ? <CheckCircle2 size={12} className="text-emerald-400" data-id="element-6249" /> : <TestTube size={12} data-id="element-6250" />}
                      {testingConnection === 'confluence' ? 'Probando...' : connectionResults['confluence'] ? 'Conexión exitosa' : 'Probar conexión'}
                    </button>
                  </div>
                </IntegrationSection>

                {/* SLACK */}
                <IntegrationSection title="Slack" icon={Sparkles} iconColor="text-purple-400" connected={slackConnected} onToggle={() => setSlackConnected(v => !v)} locked={!adminUnlocked} data-id="element-6251">
                  <div className="space-y-3" data-id="element-6252">
                    <SecretField label="Webhook URL" value={slackConfig.webhookUrl} onChange={v => setSlackConfig(p => ({
                    ...p,
                    webhookUrl: v
                  }))} placeholder="https://hooks.slack.com/services/..." hint="Creá un Incoming Webhook en api.slack.com/apps" locked={!adminUnlocked} data-id="element-6253" />

                    <SecretField label="Bot Token" value={slackConfig.botToken} onChange={v => setSlackConfig(p => ({
                    ...p,
                    botToken: v
                  }))} placeholder="xoxb-..." locked={!adminUnlocked} data-id="element-6254" />

                    <Field label="Canal por defecto" value={slackConfig.channel} onChange={v => setSlackConfig(p => ({
                    ...p,
                    channel: v
                  }))} placeholder="#dev-team" data-id="element-6255" />
                  </div>
                </IntegrationSection>

                {/* GITHUB */}
                <IntegrationSection title="GitHub" icon={Sparkles} iconColor="text-slate-300" connected={githubConnected} onToggle={() => setGithubConnected(v => !v)} locked={!adminUnlocked} data-id="element-6256">
                  <div className="space-y-3" data-id="element-6257">
                    <SecretField label="Personal Access Token" value={githubConfig.token} onChange={v => setGithubConfig(p => ({
                    ...p,
                    token: v
                  }))} placeholder="ghp_..." hint="Necesita permisos: repo, read:org" locked={!adminUnlocked} data-id="element-6258" />

                    <div className="grid grid-cols-2 gap-3" data-id="element-6259">
                      <Field label="Organización" value={githubConfig.org} onChange={v => setGithubConfig(p => ({
                      ...p,
                      org: v
                    }))} placeholder="mi-org" data-id="element-6260" />

                      <Field label="Repositorio" value={githubConfig.repo} onChange={v => setGithubConfig(p => ({
                      ...p,
                      repo: v
                    }))} placeholder="mi-repo" data-id="element-6261" />
                    </div>
                  </div>
                </IntegrationSection>

                {/* GOOGLE WORKSPACE */}
                <IntegrationSection title="Google Workspace" icon={Sparkles} iconColor="text-blue-500" connected={isGoogleConnected} onToggle={() => isGoogleConnected ? googleSignOut() : googleSignIn()} locked={false} data-id="element-6262">
                  <div className="space-y-4" data-id="element-6263">
                    {isGoogleConnected && googleUser ? <div className="flex items-center justify-between p-3 bg-slate-950 rounded-xl border border-slate-800" data-id="element-6264">
                        <div className="flex items-center gap-3" data-id="element-6265">
                          {googleUser.photoURL ? <img src={googleUser.photoURL} alt="Profile" className="w-10 h-10 rounded-full" data-id="element-6266" /> : <div className="w-10 h-10 rounded-full bg-blue-900/50 flex items-center justify-center text-blue-400 font-bold" data-id="element-6267">
                              {googleUser.displayName?.charAt(0) || 'G'}
                            </div>}
                          <div data-id="element-6268">
                            <p className="text-sm font-bold text-slate-200" data-id="element-6269">
                              {googleUser.displayName}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-6270">
                              {googleUser.email}
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={googleSignOut} className="text-red-400 hover:text-red-300 hover:bg-red-950/30 border-red-900/30" data-id="element-6271">
                          Desconectar
                        </Button>
                      </div> : <div className="text-center p-4 bg-slate-950 rounded-xl border border-slate-800" data-id="element-6272">
                        <p className="text-sm text-slate-400 mb-3" data-id="element-6273">
                          Conectá tu cuenta para habilitar Calendar, Drive y
                          Gmail.
                        </p>
                        <Button onClick={googleSignIn} className="bg-blue-600 hover:bg-blue-500 text-white" data-id="element-6274">
                          Conectar Google
                        </Button>
                      </div>}
                    <div className="space-y-2" data-id="element-6275">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-6276">
                        Permisos Otorgados
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-6277">
                        <Badge variant="outline" className="bg-blue-950/30 text-blue-400 border-blue-900/50 gap-1" data-id="element-6278">
                          <Check size={10} data-id="element-6279" /> Calendar (Read)
                        </Badge>
                        <Badge variant="outline" className="bg-emerald-950/30 text-emerald-400 border-emerald-900/50 gap-1" data-id="element-6280">
                          <Check size={10} data-id="element-6281" /> Drive (Read)
                        </Badge>
                        <Badge variant="outline" className="bg-red-950/30 text-red-400 border-red-900/50 gap-1" data-id="element-6282">
                          <Check size={10} data-id="element-6283" /> Gmail (Read)
                        </Badge>
                      </div>
                    </div>
                  </div>
                </IntegrationSection>
              </CardContent>
            </Card>

            {/* NOTIFICATION PREFERENCES */}
            <NotificationPreferences data-id="element-6284" />

            {/* AGENT CONFIG */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6285">
              <CardHeader data-id="element-6286">
                <CardTitle className="flex items-center gap-2" data-id="element-6287">
                  <Bot size={20} className="text-indigo-400" data-id="element-6288" /> Configuración
                  del Agente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-6289">
                <div data-id="element-6290">
                  <label className="block text-sm font-medium text-slate-400 mb-3" data-id="element-6291">
                    Tono de conversación
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-6292">
                    {[{
                    key: 'Professional',
                    emoji: '👔'
                  }, {
                    key: 'Friendly',
                    emoji: '😊'
                  }, {
                    key: 'Enthusiastic',
                    emoji: '🚀'
                  }, {
                    key: 'Strict',
                    emoji: '📋'
                  }].map(({
                    key,
                    emoji
                  }) => <button key={key} onClick={() => setTone(key)} className={`px-3 py-3 rounded-lg text-sm border transition-all flex flex-col items-center gap-1 ${tone === key ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-6293">
                        <span className="text-lg" data-id="element-6294">{emoji}</span>
                        <span data-id="element-6295">{key}</span>
                      </button>)}
                  </div>
                </div>
                <div data-id="element-6296">
                  <label className="block text-sm font-medium text-slate-400 mb-3" data-id="element-6297">
                    Idioma Principal
                  </label>
                  <div className="flex gap-3" data-id="element-6298">
                    {[{
                    key: 'Spanish',
                    flag: '🇪🇸'
                  }, {
                    key: 'English',
                    flag: '🇺🇸'
                  }, {
                    key: 'Portuguese',
                    flag: '🇧🇷'
                  }].map(({
                    key,
                    flag
                  }) => <button key={key} onClick={() => setLanguage(key)} className={`px-4 py-2 rounded-lg text-sm border flex items-center gap-2 transition-all ${language === key ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-6299">
                        <span data-id="element-6300">{flag}</span> {key}
                      </button>)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* APPEARANCE */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6301">
              <CardHeader data-id="element-6302">
                <CardTitle className="flex items-center gap-2" data-id="element-6303">
                  <Palette size={20} className="text-indigo-400" data-id="element-6304" /> Apariencia
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-6305">
                <div data-id="element-6306">
                  <label className="block text-sm font-medium text-slate-400 mb-3" data-id="element-6307">
                    Tema
                  </label>
                  <div className="grid grid-cols-3 gap-4" data-id="element-6308">
                    {[{
                    key: 'light' as const,
                    icon: Sun,
                    label: 'Claro'
                  }, {
                    key: 'dark' as const,
                    icon: Moon,
                    label: 'Oscuro'
                  }, {
                    key: 'system' as const,
                    icon: Monitor,
                    label: 'Sistema'
                  }].map(({
                    key,
                    icon: Icon,
                    label
                  }) => <button key={key} onClick={() => setTheme(key)} className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all ${theme === key ? 'bg-slate-800 border-indigo-500 text-indigo-400' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`} data-id="element-6309">
                        <Icon size={24} className="mb-2" data-id="element-6310" />
                        <span className="text-sm font-medium" data-id="element-6311">{label}</span>
                      </button>)}
                  </div>
                </div>
                <div data-id="element-6312">
                  <label className="block text-sm font-medium text-slate-400 mb-3" data-id="element-6313">
                    Color de acento
                  </label>
                  <div className="flex gap-4" data-id="element-6314">
                    {accentColors.map((color, idx) => <button key={idx} onClick={() => {
                    setSelectedAccent(idx);
                    updateBrand({
                      accentColor: color.value
                    });
                  }} className={`w-8 h-8 rounded-full ${color.class} ring-2 ring-offset-2 ring-offset-slate-900 transition-all ${selectedAccent === idx ? 'ring-slate-200 scale-110' : 'ring-transparent hover:ring-slate-700'}`} title={color.name} data-id="element-6315" />)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6" data-id="element-6316">
            {/* Connection Status */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6317">
              <CardHeader data-id="element-6318">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-6319">
                  <Wifi size={18} className="text-emerald-400" data-id="element-6320" /> Estado de
                  Conexión
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-id="element-6321">
                {[{
                label: 'API Status',
                value: <Badge variant="success" className="gap-1" data-id="element-6322">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-6323"></span>
                        Connected
                      </Badge>
              }, {
                label: 'Jira',
                value: <span className={`text-xs font-medium ${jiraConnected ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-6324">
                        {jiraConnected ? '✓ Activo' : '— Inactivo'}
                      </span>
              }, {
                label: 'Confluence',
                value: <span className={`text-xs font-medium ${confluenceConnected ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-6325">
                        {confluenceConnected ? '✓ Activo' : '— Inactivo'}
                      </span>
              }, {
                label: 'Latency',
                value: <span className="text-sm font-mono text-emerald-400" data-id="element-6326">
                        45ms
                      </span>
              }, {
                label: 'Region',
                value: <span className="text-sm font-mono text-slate-200" data-id="element-6327">
                        us-east-1
                      </span>
              }].map(({
                label,
                value
              }) => <div key={label} className="flex items-center justify-between p-3 bg-slate-950 rounded-lg border border-slate-800" data-id="element-6328">
                    <span className="text-sm text-slate-400" data-id="element-6329">{label}</span>
                    {value}
                  </div>)}
                <Button variant="outline" size="sm" className="w-full gap-2 text-xs mt-2" onClick={() => testConnection('api')} data-id="element-6330">
                  {testingConnection === 'api' ? <RefreshCw size={12} className="animate-spin" data-id="element-6331" /> : <RefreshCw size={12} data-id="element-6332" />}
                  Test Connection
                </Button>
              </CardContent>
            </Card>

            {/* API Endpoints */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6333">
              <CardHeader data-id="element-6334">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-6335">
                  <Building2 size={18} className="text-slate-400" data-id="element-6336" /> API &
                  Endpoints
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-id="element-6337">
                {apiEndpoints.map(ep => <div key={ep.key} className="p-2.5 bg-slate-950 rounded-lg border border-slate-800" data-id="element-6338">
                    <div className="flex items-center justify-between mb-1" data-id="element-6339">
                      <span className="text-xs text-slate-500 font-medium" data-id="element-6340">
                        {ep.label}
                      </span>
                      <button onClick={() => handleCopy(ep.value, ep.key)} className="text-slate-500 hover:text-slate-300 transition-colors p-0.5" data-id="element-6341">
                        {copiedField === ep.key ? <Check size={12} className="text-emerald-400" data-id="element-6342" /> : <Copy size={12} data-id="element-6343" />}
                      </button>
                    </div>
                    <p className="text-xs font-mono text-slate-300 truncate" data-id="element-6344">
                      {ep.value}
                    </p>
                  </div>)}
              </CardContent>
            </Card>

            {/* Security */}
            <Card className="bg-slate-900 border-slate-800" data-id="element-6345">
              <CardHeader data-id="element-6346">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-6347">
                  <Shield size={18} className="text-slate-400" data-id="element-6348" /> Seguridad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-6349">
                <div className="text-sm text-slate-400" data-id="element-6350">
                  <p className="mb-2" data-id="element-6351">
                    Autenticado vía{' '}
                    <span className="text-slate-200 font-medium" data-id="element-6352">
                      AWS Cognito
                    </span>
                    .
                  </p>
                  <p data-id="element-6353">
                    Tu sesión expira en{' '}
                    <span className="text-slate-200 font-medium" data-id="element-6354">
                      55 minutos
                    </span>
                    .
                  </p>
                </div>
                <div className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs ${adminUnlocked ? 'bg-amber-950/30 border-amber-900/40 text-amber-400' : 'bg-slate-950 border-slate-800 text-slate-500'}`} data-id="element-6355">
                  {adminUnlocked ? <>
                      <Eye size={12} data-id="element-6356" /> Modo admin activo — credenciales
                      visibles
                    </> : <>
                      <Lock size={12} data-id="element-6357" /> Credenciales enmascaradas
                    </>}
                </div>
                <Button variant="secondary" size="sm" className="w-full" onClick={onLogout} data-id="element-6358">
                  Cerrar Sesión
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}